import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import AppImage from '../../components/AppImage';
import { Checkbox } from '../../components/ui/Checkbox';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const UserRegistration = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signIn } = useAuth();

  const [isLoginMode, setIsLoginMode] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  // Check for email verification on page load
  useEffect(() => {
    const handleEmailVerification = async () => {
      // Check if this is an email verification callback
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams?.get('access_token');
      const type = hashParams?.get('type');

      if (type === 'signup' && accessToken) {
        setVerificationStatus('verifying');
        
        try {
          // Exchange the token for a session
          const { data, error } = await supabase?.auth?.getSession();
          
          if (error) throw error;
          
          if (data?.session) {
            setVerificationStatus('success');
            // Clear the hash from URL
            window.history?.replaceState(null, '', window.location?.pathname);
            
            // Redirect to user dashboard after 2 seconds
            setTimeout(() => {
              navigate('/user-dashboard');
            }, 2000);
          }
        } catch (error) {
          console.error('Verification error:', error);
          setVerificationStatus('error');
          setErrors({ submit: 'Email verification failed. Please try again or contact support.' });
        }
      }
    };

    handleEmailVerification();
  }, [navigate]);

  const validateForm = () => {
    const newErrors = {};

    if (!isLoginMode) {
      if (!formData?.fullName?.trim()) {
        newErrors.fullName = 'Full name is required';
      } else if (formData?.fullName?.trim()?.length < 2) {
        newErrors.fullName = 'Name must be at least 2 characters';
      }

      if (!formData?.phone?.trim()) {
        newErrors.phone = 'Phone number is required';
      } else if (!/^\+?[\d\s-()]{10,}$/?.test(formData?.phone)) {
        newErrors.phone = 'Please enter a valid phone number';
      }

      if (!formData?.password) {
        newErrors.password = 'Password is required';
      } else if (formData?.password?.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }

      if (formData?.password !== formData?.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }

      if (!acceptTerms) {
        newErrors.terms = 'You must accept the terms and conditions';
      }
    } else {
      if (!formData?.password) {
        newErrors.password = 'Password is required';
      }
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      if (isLoginMode) {
        // Login mode - use context's signIn to set profile immediately
        const { profile, error } = await signIn(
          formData?.email,
          formData?.password
        );

        if (error) throw error;

        if (profile?.role !== 'user') {
          await supabase?.auth?.signOut();
          setErrors({ submit: 'This login is for visitors only. Owners and contributors should use the admin login page.' });
          setIsSubmitting(false);
          return;
        }

        // Redirect to user dashboard (visitor dashboard)
        navigate('/user-dashboard');
      } else {
        // Registration mode with Supabase email verification
        const { data, error } = await supabase?.auth?.signUp({
          email: formData?.email,
          password: formData?.password,
          options: {
            data: {
              full_name: formData?.fullName,
              phone: formData?.phone,
              role: 'user'
            },
            emailRedirectTo: `${window.location?.origin}/user-registration?verified=true`
          }
        });

        if (error) throw error;

        // Show success message
        setVerificationStatus('pending');
        setRegisteredEmail(formData?.email);
        setErrors({ submit: 'Registration successful! Please check your email to verify your account. Once verified, you can sign in.' });
      }
    } catch (error) {
      setErrors({
        submit: error?.message || 'An error occurred. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    if (!registeredEmail) return;
    
    setResendingEmail(true);
    try {
      const { error } = await supabase?.auth?.resend({
        type: 'signup',
        email: registeredEmail,
      });
      
      if (error) throw error;
      
      setErrors({ submit: 'Verification email resent! Please check your inbox.' });
    } catch (error) {
      setErrors({ submit: error?.message || 'Failed to resend verification email.' });
    } finally {
      setResendingEmail(false);
    }
  };

  // Show verification success screen
  if (verificationStatus === 'success') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="bg-card border border-border rounded-2xl shadow-warm-lg p-8 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-success/10 mb-6">
              <Icon name="CheckCircle" size={48} color="var(--color-success)" />
            </div>
            <h2 className="text-2xl font-bold mb-3">Email Verified Successfully!</h2>
            <p className="text-muted-foreground mb-6">
              Your account has been verified. Redirecting you to your dashboard...
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Icon name="Loader2" size={16} className="animate-spin" />
              <span>Loading your dashboard</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show verification in progress
  if (verificationStatus === 'verifying') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="bg-card border border-border rounded-2xl shadow-warm-lg p-8 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-6">
              <Icon name="Loader2" size={48} color="var(--color-primary)" className="animate-spin" />
            </div>
            <h2 className="text-2xl font-bold mb-3">Verifying Your Email...</h2>
            <p className="text-muted-foreground">
              Please wait while we confirm your email address.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AppImage
              src="/assets/images/RVS_InitialLogo_01-02-1768324259986.png"
              alt="RVS Gallery Platform Logo"
              className="h-10 w-auto"
            />
            <div>
              <h1 className="text-xl font-bold">RVS Gallery</h1>
              <p className="text-xs text-muted-foreground">Visitor Access</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Card */}
          <div className="bg-card border border-border rounded-2xl shadow-warm-lg p-8">
            {/* Title */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                <Icon name="Users" size={32} color="var(--color-primary)" />
              </div>
              <h2 className="text-2xl font-bold mb-2">
                {isLoginMode ? 'Welcome Back' : 'Create Your Account'}
              </h2>
              <p className="text-muted-foreground">
                {isLoginMode 
                  ? 'Sign in to access your galleries' :'Register to access and manage your gallery collection'
                }
              </p>
            </div>

            {/* Benefits (only show in registration mode) */}
            {!isLoginMode && verificationStatus !== 'pending' && (
              <div className="mb-6 p-4 bg-muted/50 rounded-xl border border-border">
                <p className="text-sm font-medium mb-2">Benefits of Registration:</p>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <Icon name="Check" size={16} className="mt-0.5 flex-shrink-0" color="var(--color-success)" />
                    <span>Access multiple galleries with one account</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon name="Check" size={16} className="mt-0.5 flex-shrink-0" color="var(--color-success)" />
                    <span>Track your gallery access history</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon name="Check" size={16} className="mt-0.5 flex-shrink-0" color="var(--color-success)" />
                    <span>Enter galleries using unique codes</span>
                  </li>
                </ul>
              </div>
            )}

            {/* Show pending verification message */}
            {verificationStatus === 'pending' && (
              <div className="mb-6">
                <div className="p-4 bg-primary/10 rounded-xl border border-primary/20 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/20 mb-3">
                    <Icon name="Mail" size={24} color="var(--color-primary)" />
                  </div>
                  <h3 className="font-semibold mb-2">Check Your Email</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    We've sent a verification link to <strong>{formData?.email}</strong>. 
                    Click the link in the email to verify your account.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Once verified, return here to sign in.
                  </p>
                </div>
                <div className="mt-6 text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setVerificationStatus(null);
                      setIsLoginMode(true);
                      setErrors({});
                    }}
                    className="text-sm text-primary hover:underline"
                  >
                    Already verified? Sign In
                  </button>
                </div>
              </div>
            )}

            {/* Form - hide when verification pending */}
            {verificationStatus !== 'pending' && (
              <>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {!isLoginMode && (
                    <Input
                      label="Full Name"
                      type="text"
                      placeholder="John Doe"
                      value={formData?.fullName}
                      onChange={(e) => handleInputChange('fullName', e?.target?.value)}
                      error={errors?.fullName}
                      required
                    />
                  )}

                  <Input
                    label="Email Address"
                    type="email"
                    placeholder="your.email@example.com"
                    value={formData?.email}
                    onChange={(e) => handleInputChange('email', e?.target?.value)}
                    error={errors?.email}
                    required
                  />

                  {!isLoginMode && (
                    <Input
                      label="Phone Number"
                      type="tel"
                      placeholder="+1 (555) 123-4567"
                      value={formData?.phone}
                      onChange={(e) => handleInputChange('phone', e?.target?.value)}
                      error={errors?.phone}
                      required
                    />
                  )}

                  <Input
                    label="Password"
                    type="password"
                    placeholder={isLoginMode ? "Enter your password" : "Create a strong password (min 8 characters)"}
                    value={formData?.password}
                    onChange={(e) => handleInputChange('password', e?.target?.value)}
                    error={errors?.password}
                    required
                  />

                  {!isLoginMode && (
                    <Input
                      label="Confirm Password"
                      type="password"
                      placeholder="Re-enter your password"
                      value={formData?.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
                      error={errors?.confirmPassword}
                      required
                    />
                  )}

                  {!isLoginMode && (
                    <div className="flex items-start gap-2">
                      <Checkbox
                        id="acceptTerms"
                        checked={acceptTerms}
                        onChange={(checked) => {
                          setAcceptTerms(checked);
                          if (errors?.terms) {
                            setErrors(prev => ({ ...prev, terms: '' }));
                          }
                        }}
                      />
                      <label htmlFor="acceptTerms" className="text-sm text-muted-foreground cursor-pointer">
                        I accept the terms of service and privacy policy
                      </label>
                    </div>
                  )}
                  {errors?.terms && (
                    <p className="text-sm text-destructive">{errors?.terms}</p>
                  )}

                  {errors?.submit && (
                    <div className={`p-4 rounded-lg flex items-start gap-3 ${
                      verificationStatus === 'pending' ?'bg-primary/10 border border-primary/20' :'bg-destructive/10 border border-destructive/20'
                    }`}>
                      <Icon 
                        name={verificationStatus === 'pending' ? 'Mail' : 'AlertCircle'} 
                        size={20} 
                        className={`mt-0.5 flex-shrink-0 ${
                          verificationStatus === 'pending' ? 'text-primary' : 'text-destructive'
                        }`} 
                      />
                      <div className="flex-1">
                        <p className={`text-sm ${
                          verificationStatus === 'pending' ? 'text-primary' : 'text-destructive'
                        }`}>
                          {errors?.submit}
                        </p>
                        {verificationStatus === 'pending' && registeredEmail && (
                          <button
                            type="button"
                            onClick={handleResendVerification}
                            disabled={resendingEmail}
                            className="mt-2 text-sm font-medium text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {resendingEmail ? 'Resending...' : 'Resend verification email'}
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    variant="default"
                    className="w-full"
                    loading={isSubmitting}
                    iconName={isLoginMode ? "LogIn" : "UserPlus"}
                    iconPosition="right"
                  >
                    {isLoginMode ? 'Sign In' : 'Create Account'}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <button
                    type="button"
                    onClick={() => navigate('/')}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    ← Back to Welcome Page
                  </button>
                </div>

                {/* Toggle Mode */}
                <div className="mt-6 text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setIsLoginMode(!isLoginMode);
                      setErrors({});
                      setFormData({
                        fullName: '',
                        email: '',
                        phone: '',
                        password: '',
                        confirmPassword: ''
                      });
                      setAcceptTerms(false);
                    }}
                    className="text-sm text-primary hover:underline"
                  >
                    {isLoginMode 
                      ? "Don't have an account? Register" :'Already have an account? Sign In'
                    }
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Additional Info */}
          <div className="mt-6 text-center text-sm text-muted-foreground">
            <p>Need help? Contact the gallery owner or administrator</p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserRegistration;